/*
  Value-Added Solutions — Solar Box Solutions page
  ─────────────────────────────────────────────────
  Kept as its own deferred asset file (rather than inline in the section,
  and rather than folded into shared theme JS) because this page's
  interactivity — sizing calculator, 7-step CCTV pre-check form, tabs,
  carousels, two modals — is substantial and page-specific. A separate
  cached file keeps the initial page HTML light and non-blocking.

  Scoped to run once per `[data-vas-solarbox-root]` element on the page
  (normally just one), so nothing here leaks into other sections.
*/
(function () {
  'use strict';

  function initRoot(root) {
    var hsPortalId = root.getAttribute('data-hs-portal-id') || '';
    var hsFormId = root.getAttribute('data-hs-form-id') || '';
    var precheckHsPortalId = root.getAttribute('data-precheck-hs-portal-id') || '';
    var precheckHsFormId = root.getAttribute('data-precheck-hs-form-id') || '';

    // On mobile, a newly-revealed detail panel sits below the fold and isn't
    // obviously "there" until the user scrolls — nudge them down to it.
    // Matches the theme's 750px CSS breakpoint, so desktop (where the panel
    // is already visible right below the tiles) is left alone.
    function scrollDetailIntoViewOnMobile(el) {
      if (window.innerWidth >= 750) return;
      // Double rAF: waits a full extra paint cycle so the browser has
      // definitely committed the display:none -> block layout change
      // before we calculate where to scroll to. A single rAF is not
      // always enough on mobile browsers and can scroll to a stale
      // (pre-layout) position.
      window.requestAnimationFrame(function () {
        window.requestAnimationFrame(function () {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        });
      });
    }

    /* ── HubSpot submission (shared by all forms in this section) ── */
    function submitToHubSpot(form, msgEl, btn, fieldNameWhitelist, portalIdOverride, formIdOverride) {
      var targetPortalId = portalIdOverride || hsPortalId;
      var targetFormId = formIdOverride || hsFormId;
      // Honeypot check — if the hidden field has been filled in, silently
      // treat as spam: no HubSpot request, but show a normal success state
      // so bots don't learn the field is being checked.
      var hp = form.querySelector('.vas-hp-input');
      if (hp && hp.value.trim() !== '') {
        msgEl.style.display = 'block';
        msgEl.innerHTML = '<p style="color:#2ea84a;font-weight:600;font-size:1.3rem;padding:1rem;background:#f2fff5;border-left:3px solid #2ea84a;border-radius:0 8px 8px 0">&#10003; Enquiry submitted successfully. A Powerbox specialist will be in touch shortly.</p>';
        form.reset();
        return;
      }

      var missing = [];
      Array.prototype.forEach.call(form.elements, function (el) {
        if (!el.name || el.type === 'submit' || !el.required) return;
        if (!el.value || el.value.trim() === '') {
          missing.push(el);
          el.style.borderColor = 'var(--pbx-color-brand-red, #DB2727)';
        } else {
          el.style.borderColor = '';
        }
      });
      if (missing.length > 0) {
        msgEl.style.display = 'block';
        msgEl.innerHTML = '<p style="color:var(--pbx-color-brand-red, #DB2727);font-size:1.3rem;padding:1rem;background:#fdf2f2;border-left:3px solid var(--pbx-color-brand-red, #DB2727);border-radius:0 8px 8px 0">Please complete all required fields before submitting.</p>';
        missing[0].focus();
        return;
      }
      Array.prototype.forEach.call(form.elements, function (el) { el.style.borderColor = ''; });
      msgEl.style.display = 'none';

      if (!targetPortalId || !targetFormId) {
        msgEl.style.display = 'block';
        msgEl.innerHTML = '<p style="color:var(--pbx-color-brand-red, #DB2727);font-size:1.3rem">This form is not yet connected to HubSpot — set the Portal ID and Form ID in the section settings.</p>';
        return;
      }

      var fields = [];
      Array.prototype.forEach.call(form.elements, function (el) {
        if (!el.name || el.type === 'submit') return;
        if (el.classList && el.classList.contains('vas-hp-input')) return; // never send the honeypot value
        if (fieldNameWhitelist && fieldNameWhitelist.indexOf(el.name) === -1) return; // only send known HubSpot properties, when a whitelist is given
        if ((el.type === 'checkbox' || el.type === 'radio') && !el.checked) return;
        fields.push({ name: el.name, value: el.value });
      });
      var payload = { fields: fields, context: { pageUri: window.location.href, pageName: document.title } };

      btn.disabled = true;
      var originalText = btn.textContent;
      btn.textContent = 'Submitting…';
      msgEl.style.display = 'none';

      fetch('https://api.hsforms.com/submissions/v3/integration/submit/' + targetPortalId + '/' + targetFormId, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      })
        .then(function (res) {
          if (res.ok) {
            msgEl.style.display = 'block';
            msgEl.innerHTML = '<p style="color:#2ea84a;font-weight:600;font-size:1.3rem;padding:1rem;background:#f2fff5;border-left:3px solid #2ea84a;border-radius:0 8px 8px 0">&#10003; Enquiry submitted successfully. A Powerbox specialist will be in touch shortly.</p>';
            form.reset();
            var successEl = form.parentElement ? form.parentElement.querySelector('.js-vas-precheck-success') : null;
            if (successEl) {
              form.style.display = 'none';
              var stepper = root.querySelector('.js-vas-precheck-stepper');
              if (stepper) stepper.style.display = 'none';
              var introEl = root.querySelector('.js-vas-precheck-intro');
              if (introEl) introEl.style.display = 'none';
              successEl.classList.add('is-shown');
              // Hiding the form/stepper/intro collapses this card's height a
              // lot — without this, the user's scroll position (often near
              // the bottom of the long form when they hit Submit) ends up
              // pointing at whatever unrelated section now occupies that
              // same spot on the page.
              window.requestAnimationFrame(function () {
                window.requestAnimationFrame(function () {
                  successEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
                });
              });
            }
          } else {
            return res.json().then(function (e) { throw e; });
          }
        })
        .catch(function () {
          msgEl.style.display = 'block';
          msgEl.innerHTML = '<p style="color:var(--pbx-color-brand-red, #DB2727);font-size:1.3rem;padding:1rem;background:#fdf2f2;border-left:3px solid var(--pbx-color-brand-red, #DB2727);border-radius:0 8px 8px 0">Something went wrong. Please try again or contact us directly.</p>';
        })
        .finally(function () { btn.disabled = false; btn.textContent = originalText; });
    }

    /* ── Industry conditional field (bottom form + engineer modal) ── */
    root.querySelectorAll('.js-vas-industry-select').forEach(function (select) {
      var wrap = select.closest('form').querySelector('.js-vas-industry-other-wrap');
      var input = wrap ? wrap.querySelector('input') : null;
      function sync() {
        var isOther = select.value === 'Other';
        if (wrap) wrap.classList.toggle('is-shown', isOther);
        if (input) input.required = isOther;
      }
      select.addEventListener('change', sync);
      sync();
    });

    /* ── Bottom contact form — progressive disclosure + submit ── */
    var bottomForm = root.querySelector('.js-vas-bottom-form');
    if (bottomForm) {
      var triggerFields = bottomForm.querySelectorAll('.js-vas-pf-trigger');
      var reveal = function () { bottomForm.classList.add('is-expanded'); };
      triggerFields.forEach(function (f) { f.addEventListener('focus', reveal, { once: true }); });
      bottomForm.addEventListener('submit', function (e) {
        e.preventDefault();
        submitToHubSpot(bottomForm, bottomForm.querySelector('.js-vas-form-msg'), bottomForm.querySelector('button[type="submit"]'));
      });
    }

    /* ── Talk to an Engineer modal ── */
    var engineerOverlay = root.querySelector('.js-vas-engineer-modal-overlay');
    var engineerForm = root.querySelector('.js-vas-engineer-form');
    function openEngineerModal() { if (engineerOverlay) { engineerOverlay.classList.add('is-open'); document.body.style.overflow = 'hidden'; } }
    function closeEngineerModal() { if (engineerOverlay) { engineerOverlay.classList.remove('is-open'); document.body.style.overflow = ''; } }
    root.querySelectorAll('.js-vas-talk-engineer').forEach(function (btn) {
      btn.addEventListener('click', function (e) { e.preventDefault(); openEngineerModal(); });
    });
    if (engineerOverlay) {
      var engineerClose = engineerOverlay.querySelector('.js-vas-engineer-modal-close');
      if (engineerClose) engineerClose.addEventListener('click', closeEngineerModal);
      engineerOverlay.addEventListener('click', function (e) { if (e.target === engineerOverlay) closeEngineerModal(); });
    }
    if (engineerForm) {
      engineerForm.addEventListener('submit', function (e) {
        e.preventDefault();
        submitToHubSpot(engineerForm, engineerForm.querySelector('.js-vas-form-msg'), engineerForm.querySelector('button[type="submit"]'));
      });
    }

    /* ── CCTV Pre-Check & Sizing form ── */
    // All the technical fields (deployment, load profile, autonomy, environment,
    // comms, mounting, budget, timeline) get compiled into one readable summary
    // rather than submitted as individual HubSpot properties — this form maps
    // to the standard contact fields plus a dedicated "project_details"
    // property, matching the pattern used on the Access Control Panel
    // Integration page.
    var precheckContactFieldWhitelist = ['firstname', 'lastname', 'company', 'email', 'phone', 'project_details', 'solution_type'];

    function buildPrecheckSummary(form) {
      function val(name) {
        var el = form.querySelector('[name="' + name + '"]');
        return el && el.value.trim() !== '' ? el.value.trim() : '—';
      }
      function checkedValues(name) {
        var els = form.querySelectorAll('[name="' + name + '"]:checked');
        var vals = Array.prototype.map.call(els, function (el) { return el.value; });
        return vals.length ? vals.join(', ') : '—';
      }
      function radioValue(name) {
        var el = form.querySelector('[name="' + name + '"]:checked');
        return el ? el.value : '—';
      }

      var lines = [];
      lines.push('CCTV PRE-CHECK & SIZING FORM — Solar Box Solutions');
      lines.push('');

      lines.push('DEPLOYMENT PROFILE');
      lines.push('Deployment type: ' + checkedValues('deployment_type'));
      lines.push('Address / GPS coordinates: ' + val('address_gps'));
      lines.push('Operating region(s) / state(s): ' + val('operating_region'));
      lines.push('Harshest-case location: ' + val('harshest_location'));
      lines.push('Expected relocation frequency: ' + val('relocation_frequency'));
      lines.push('');

      lines.push('LOAD PROFILE');
      var deviceTypes = form.querySelectorAll('[name="device_type[]"]');
      var deviceQtys = form.querySelectorAll('[name="device_qty[]"]');
      var devicePowers = form.querySelectorAll('[name="device_power[]"]');
      var deviceVoltages = form.querySelectorAll('[name="device_voltage[]"]');
      var deviceHours = form.querySelectorAll('[name="device_hours[]"]');
      var deviceLines = [];
      for (var i = 0; i < deviceTypes.length; i++) {
        var type = deviceTypes[i].value.trim();
        if (!type) continue;
        deviceLines.push(
          '- ' + type +
          ' | Qty: ' + (deviceQtys[i] && deviceQtys[i].value.trim() !== '' ? deviceQtys[i].value.trim() : '—') +
          ' | Power: ' + (devicePowers[i] && devicePowers[i].value.trim() !== '' ? devicePowers[i].value.trim() + 'W' : '—') +
          ' | Voltage: ' + (deviceVoltages[i] && deviceVoltages[i].value.trim() !== '' ? deviceVoltages[i].value.trim() : '—') +
          ' | Hours/day: ' + (deviceHours[i] && deviceHours[i].value.trim() !== '' ? deviceHours[i].value.trim() : '—')
        );
      }
      lines.push(deviceLines.length ? deviceLines.join('\n') : '—');
      lines.push('');

      lines.push('AUTONOMY & BATTERY');
      lines.push('Required autonomy (days, zero solar): ' + val('autonomy_days'));
      lines.push('Fully self-contained (battery + solar only): ' + radioValue('self_contained'));
      lines.push('Fuel cell backup acceptable: ' + radioValue('fuel_cell_ok'));
      lines.push('Preferred battery chemistry: ' + radioValue('battery_chemistry'));
      lines.push('');

      lines.push('ENVIRONMENTAL CONDITIONS');
      lines.push('Ambient temperature range: ' + val('temp_min') + ' to ' + val('temp_max'));
      lines.push('Site exposure: ' + checkedValues('site_exposure'));
      lines.push('');

      lines.push('COMMUNICATIONS');
      lines.push('Backhaul type: ' + radioValue('backhaul_type'));
      lines.push('Data transmission pattern: ' + radioValue('transmission_pattern'));
      lines.push('Remote monitoring of power supply required: ' + radioValue('remote_monitoring'));
      lines.push('');

      lines.push('MECHANICAL & MOUNTING');
      lines.push('Enclosure mounting: ' + radioValue('mounting_type'));
      lines.push('Equipment space required: ' + val('equipment_space'));
      lines.push('');

      lines.push('COMMERCIAL');
      lines.push('Budget range: ' + val('precheck_budget_range'));
      lines.push('Timeline / lead time required: ' + val('precheck_timeline'));

      return lines.join('\n');
    }

    var precheckForm = root.querySelector('.js-vas-precheck-form');
    if (precheckForm) {
      precheckForm.addEventListener('submit', function (e) {
        e.preventDefault();
        var summaryField = precheckForm.querySelector('.js-vas-precheck-summary-preview');
        if (summaryField) summaryField.value = buildPrecheckSummary(precheckForm);
        submitToHubSpot(precheckForm, precheckForm.querySelector('.js-vas-precheck-msg'), precheckForm.querySelector('.js-vas-precheck-submit'), precheckContactFieldWhitelist, precheckHsPortalId, precheckHsFormId);
      });
    }

    var precheckCard = root.querySelector('.js-vas-precheck-card');
    var precheckToggle = root.querySelector('.js-vas-precheck-toggle');
    if (precheckToggle && precheckCard) {
      precheckToggle.addEventListener('click', function () { precheckCard.classList.toggle('is-open'); });
    }
    root.querySelectorAll('.js-vas-precheck-trigger').forEach(function (trigger) {
      trigger.addEventListener('click', function (e) {
        e.preventDefault();
        if (precheckCard) precheckCard.classList.add('is-open');
        var targetId = (trigger.getAttribute('href') || '').replace('#', '');
        var precheckSection = targetId ? document.getElementById(targetId) : null;
        if (precheckSection) precheckSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    });

    var precheckStepper = root.querySelector('.js-vas-precheck-stepper');
    if (precheckStepper) {
      var totalSteps = parseInt(precheckStepper.getAttribute('data-total-steps'), 10) || 1;
      var currentStep = 1;
      function setPrecheckStep(n) {
        currentStep = n;
        root.querySelectorAll('.js-vas-precheck-dot').forEach(function (d) {
          var step = parseInt(d.getAttribute('data-step'), 10);
          d.classList.toggle('is-active', step === n);
          d.classList.toggle('is-complete', step < n);
        });
        root.querySelectorAll('.vas-precheck-panel').forEach(function (p) {
          p.classList.toggle('is-active', parseInt(p.getAttribute('data-panel'), 10) === n);
        });
        var prevBtn = root.querySelector('.js-vas-precheck-prev');
        var nextBtn = root.querySelector('.js-vas-precheck-next');
        var submitBtn = root.querySelector('.js-vas-precheck-submit');
        if (prevBtn) prevBtn.style.visibility = n === 1 ? 'hidden' : 'visible';
        if (nextBtn) nextBtn.style.display = n === totalSteps ? 'none' : 'inline-flex';
        if (submitBtn) submitBtn.style.display = n === totalSteps ? 'inline-flex' : 'none';
        if (n === totalSteps) {
          var summaryField = root.querySelector('.js-vas-precheck-summary-preview');
          if (summaryField && typeof buildPrecheckSummary === 'function') summaryField.value = buildPrecheckSummary(precheckForm);
        }
      }
      root.querySelectorAll('.js-vas-precheck-dot').forEach(function (d) {
        d.addEventListener('click', function () { setPrecheckStep(parseInt(d.getAttribute('data-step'), 10)); });
      });
      var precheckPrev = root.querySelector('.js-vas-precheck-prev');
      var precheckNext = root.querySelector('.js-vas-precheck-next');
      if (precheckPrev) precheckPrev.addEventListener('click', function () { if (currentStep > 1) setPrecheckStep(currentStep - 1); });
      if (precheckNext) precheckNext.addEventListener('click', function () { if (currentStep < totalSteps) setPrecheckStep(currentStep + 1); });
    }

    var addRowBtn = root.querySelector('.js-vas-precheck-add-row');
    if (addRowBtn) {
      addRowBtn.addEventListener('click', function () {
        var tbody = root.querySelector('.js-vas-precheck-device-tbody');
        if (!tbody) return;
        var row = document.createElement('tr');
        row.innerHTML =
          '<td><input type="text" name="device_type[]" placeholder="Device type"></td>' +
          '<td><input type="number" name="device_qty[]" min="0"></td>' +
          '<td><input type="number" name="device_power[]" min="0"></td>' +
          '<td><input type="text" name="device_voltage[]" placeholder="12V"></td>' +
          '<td><input type="number" name="device_hours[]" min="0" max="24"></td>';
        tbody.appendChild(row);
      });
    }

    /* ── Sizing calculator ── */
    var calcCard = root.querySelector('.js-vas-calc-card');
    var calcToggle = root.querySelector('.js-vas-calc-toggle');
    if (calcToggle && calcCard) {
      calcToggle.addEventListener('click', function () { calcCard.classList.toggle('is-open'); });
    }
    var calcRunBtn = root.querySelector('.js-vas-calc-run');
    if (calcRunBtn) {
      calcRunBtn.addEventListener('click', function () {
        var load = parseFloat(root.querySelector('.js-vas-calc-load').value) || 0;
        var hours = parseFloat(root.querySelector('.js-vas-calc-hours').value) || 0;
        var autonomy = parseFloat(root.querySelector('.js-vas-calc-autonomy').value) || 0;
        var voltageSelect = root.querySelector('.js-vas-calc-voltage');
        var voltage = parseFloat(voltageSelect.value) || 24;
        var regionSelect = root.querySelector('.js-vas-calc-region');
        var psh = parseFloat(regionSelect.value) || 2.3;
        var regionLabel = regionSelect.options[regionSelect.selectedIndex].text;

        var dailyWh = load * hours;
        var usableDoD = 0.8;
        var batteryAh = (dailyWh * autonomy) / (voltage * usableDoD);
        var systemDerate = 0.75;
        var panelWp = dailyWh / (psh * systemDerate);

        root.querySelector('.js-vas-result-wh').textContent = Math.round(dailyWh) + ' Wh/day';
        root.querySelector('.js-vas-result-ah').textContent = Math.round(batteryAh) + ' Ah @ ' + voltage + 'V';
        root.querySelector('.js-vas-result-ah-note').textContent = 'Sized for ' + autonomy + ' day' + (autonomy == 1 ? '' : 's') + ' of autonomy, not region';
        root.querySelector('.js-vas-result-wp').textContent = Math.round(panelWp) + ' Wp';
        root.querySelector('.js-vas-result-wp-note').textContent = 'Based on winter sun hours for: ' + regionLabel;
        root.querySelector('.js-vas-calc-results').classList.add('is-shown');
      });
    }

    /* ── Topic selector tabs (One Supplier) ── */
    root.querySelectorAll('.js-vas-topic-btn').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var topic = btn.getAttribute('data-topic');
        root.querySelectorAll('.js-vas-topic-btn').forEach(function (b) { b.classList.remove('is-active'); });
        root.querySelectorAll('.js-vas-topic-panel').forEach(function (p) { p.classList.remove('is-active'); });
        btn.classList.add('is-active');
        var panel = root.querySelector('.js-vas-topic-panel[data-topic="' + topic + '"]');
        if (panel) panel.classList.add('is-active');
      });
    });

    /* ── Application tiles (Common Applications) ── */
    root.querySelectorAll('.js-vas-app-tile').forEach(function (tile) {
      tile.addEventListener('click', function (e) {
        e.preventDefault();
        var app = tile.getAttribute('data-app');
        root.querySelectorAll('.js-vas-app-tile').forEach(function (t) { t.classList.remove('is-active'); });
        root.querySelectorAll('.js-vas-app-detail').forEach(function (d) { d.classList.remove('is-active'); });
        tile.classList.add('is-active');
        var detail = root.querySelector('.js-vas-app-detail[data-app="' + app + '"]');
        if (detail) {
          detail.classList.add('is-active');
          scrollDetailIntoViewOnMobile(detail);
        }
      });
    });

    /* ── Product tiles (What Goes Into a Solar Box Solution) ── */
    root.querySelectorAll('.js-vas-product-tile').forEach(function (tile) {
      tile.addEventListener('click', function (e) {
        e.preventDefault();
        var product = tile.getAttribute('data-product');
        root.querySelectorAll('.js-vas-product-tile').forEach(function (t) { t.classList.remove('is-active'); });
        root.querySelectorAll('.js-vas-product-detail').forEach(function (d) { d.classList.remove('is-active'); });
        tile.classList.add('is-active');
        var detail = root.querySelector('.js-vas-product-detail[data-product="' + product + '"]');
        if (detail) {
          detail.classList.add('is-active');
          scrollDetailIntoViewOnMobile(detail);
        }
      });
    });

    /* ── FAQ accordion ── */
    root.querySelectorAll('.vas-faq-q').forEach(function (q) {
      q.addEventListener('click', function () { q.closest('.vas-faq-item').classList.toggle('is-open'); });
    });

    /* ── Carousels — generic prev/next by data-carousel key ── */
    root.querySelectorAll('.js-vas-carousel-prev, .js-vas-carousel-next').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var key = btn.getAttribute('data-carousel');
        var track = root.querySelector('.js-vas-carousel[data-carousel="' + key + '"]');
        if (!track) return;
        var amount = track.firstElementChild ? track.firstElementChild.offsetWidth + 16 : 300;
        track.scrollBy({ left: btn.classList.contains('js-vas-carousel-prev') ? -amount : amount, behavior: 'smooth' });
      });
    });

    /* ── Project detail modal ── */
    var projectOverlay = root.querySelector('.js-vas-project-modal-overlay');
    if (projectOverlay) {
      function closeProjectModal() { projectOverlay.classList.remove('is-open'); document.body.style.overflow = ''; }

      var pmImages = [];
      var pmCurrentImg = 0;

      function pmShowImage(idx) {
        var mediaEl = projectOverlay.querySelector('.js-vas-pm-media');
        var counter = projectOverlay.querySelector('.js-vas-pm-img-counter');
        var nav = projectOverlay.querySelector('.js-vas-pm-img-nav');
        if (!pmImages.length) return;
        pmCurrentImg = (idx + pmImages.length) % pmImages.length;
        var url = pmImages[pmCurrentImg];
        var isVideo = /\.(mp4|webm|ogg|mov)(\?|$)/i.test(url);
        if (isVideo) {
          var existingImg = mediaEl.querySelector('.pm-img-display');
          if (existingImg) existingImg.remove();
          var vid = mediaEl.querySelector('video') || document.createElement('video');
          vid.className = 'pm-img-display'; vid.src = url; vid.controls = true; vid.setAttribute('playsinline', '');
          vid.style.cssText = 'position:absolute;inset:0;width:100%;height:100%;object-fit:contain;display:block;';
          mediaEl.appendChild(vid);
        } else {
          var existingVid = mediaEl.querySelector('video');
          if (existingVid) existingVid.remove();
          var img = mediaEl.querySelector('.pm-img-display') || document.createElement('img');
          img.className = 'pm-img-display'; img.src = url; img.alt = '';
          img.style.cssText = 'position:absolute;inset:0;width:100%;height:100%;object-fit:contain;display:block;';
          mediaEl.appendChild(img);
        }
        if (pmImages.length > 1) {
          counter.textContent = (pmCurrentImg + 1) + ' / ' + pmImages.length;
          counter.style.display = '';
          nav.style.display = 'flex';
        } else {
          counter.style.display = 'none';
          nav.style.display = 'none';
        }
      }

      var pmPrev = projectOverlay.querySelector('.js-vas-pm-img-prev');
      var pmNext = projectOverlay.querySelector('.js-vas-pm-img-next');
      if (pmPrev) pmPrev.addEventListener('click', function (e) { e.stopPropagation(); pmShowImage(pmCurrentImg - 1); });
      if (pmNext) pmNext.addEventListener('click', function (e) { e.stopPropagation(); pmShowImage(pmCurrentImg + 1); });

      root.querySelectorAll('.js-vas-project-card').forEach(function (card) {
        card.addEventListener('click', function () {
          try { pmImages = JSON.parse(card.dataset.mediaUrls || '[]'); } catch (e) { pmImages = []; }
          pmCurrentImg = 0;
          var mediaEl = projectOverlay.querySelector('.js-vas-pm-media');
          mediaEl.querySelectorAll('.pm-img-display, video').forEach(function (el) { el.remove(); });
          pmShowImage(0);
          projectOverlay.querySelector('.js-vas-pm-sub').textContent = card.dataset.sub || '';
          projectOverlay.querySelector('.js-vas-pm-title').textContent = card.dataset.title || '';
          projectOverlay.querySelector('.js-vas-pm-desc').innerHTML = (card.dataset.desc || '').replace(/\n\n+/g, '<br><br>').replace(/\n/g, '<br>');
          var ctaLink = projectOverlay.querySelector('.js-vas-pm-cta');
          var ctaBtn = projectOverlay.querySelector('.js-vas-pm-cta-btn');
          if (card.dataset.hasCta === 'true') {
            ctaLink.href = card.dataset.ctaUrl;
            ctaBtn.textContent = card.dataset.ctaLabel || 'View Complete Case Study';
            ctaLink.style.display = '';
          } else {
            ctaLink.style.display = 'none';
          }
          projectOverlay.classList.add('is-open');
          document.body.style.overflow = 'hidden';
        });
      });
      var projectClose = projectOverlay.querySelector('.js-vas-project-modal-close');
      if (projectClose) projectClose.addEventListener('click', closeProjectModal);
      projectOverlay.addEventListener('click', function (e) { if (e.target === projectOverlay) closeProjectModal(); });
    }
  }

  function init() {
    document.querySelectorAll('[data-vas-solarbox-root]').forEach(initRoot);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
